import { ResourceTypes } from "./app.service";

export interface BaseDataResponse<T> {
  data: T;
  status: string;
  message: string;
}

export interface PaginatedDataResponse<T> extends BaseDataResponse<T> {
  links: Links;
  meta: Meta;
}

export interface PaginatedDataWithObjectResponse<
  T,
> extends PaginatedDataResponse<T> {
  content_class?: ClassResponse;
  test_class?: ClassResponse;
}

export interface AuthResponse extends UserResponse {
  access_token: string;
}

export interface UserResponse extends DateTimeResponse {
  id: number;
  ecampus_id: string;
  name: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  country: string;
  city: string;
  phone_number: string;
  image_url: string;
  last_login: string;
  active: boolean;
  points: number;
  institute?: string;
  referral_id?: boolean;
  third_party: string;
  firebase_uid: string;
  about: string;
  gender?: string;
}

export interface DateTimeResponse {
  created_at: string;
  updated_at: string;
}

export interface InterestResponse extends DateTimeResponse {
  id: number;
  name: string;
  total_classes: number;
  link_name: string;
}

export interface CategoryResponse extends InterestResponse {
  level: number;
}

export interface Links {
  first: string;
  last: string;
  prev?: string;
  next?: string;
}

export interface Meta {
  current_page: number;
  from: number;
  last_page: number;
  path: string;
  per_page: number;
  to: number;
  total: number;
  links: Array<MetaLink>;
}

export interface MetaLink {
  url?: string;
  label: string;
  active: boolean;
}

export interface CourseResponse {
  id: number;
  class_id: number;
  title: string;
  description: string;
  published: boolean;
  total_topics: number;
  has_read: boolean;
  has_practice: boolean;
  created_at?: string;
  updated_at?: string;
  content_class?: ClassResponse;
  topics?: CourseResponse[];
  link_name?: string;
}

export interface SubscriptionResponse extends DateTimeResponse {
  id: number;
  active: boolean;
  has_paid: boolean;
  has_expired: boolean;
  is_subscribed: boolean;
  start_date?: string;
  end_date?: string;
  subscriber_class: ClassResponse;
}

export interface PaidSubscriptionResponse extends DateTimeResponse {
  id: number;
  subscriber_id: number;
  paid: boolean;
  start: string;
  end: string;
  details: string;
  duration: number;
  payment_type: string;
  payment_date: string;
  payment_token?: string;
  payment_transaction_id?: string;
  payment_amount?: string;
  payment_currency?: string;
  subscriber: SubscriptionResponse;
}

export interface ClassResponse extends DateTimeResponse {
  id: number;
  created_by?: number;
  category_id: number;
  content_provider_id: number;
  name: string;
  description: string;
  subscription_amount_year: number;
  subscription_amount_years: number;
  subscription_amount_month: number;
  subscription_currency: string;
  logo?: string;
  link_name: string;
  public: boolean;
  active: boolean;
  logo_url: string;
  total_subscribers: number;
  total_courses?: number;
  total_topics?: number;
  free_period?: number;
  content_provider: ContentProviderResponse;
  category?: CategoryResponse;
  interests: Array<InterestResponse>;
  skills: Array<String>;
  goals: Array<String>;
  requirements: Array<String>;
  runtime: number;
  total_audios: number;
  total_videos: number;
  total_tests: number;
  total_questions: number;
  contents_accessed: number | null;
  has_paid: boolean;
  has_expired: boolean;
  is_subscribed: boolean;
  is_free: boolean;
  language: string;
  payment_url?: string;
  rating: number;
}

export interface ContentProviderResponse {
  id: number;
  name: string;
  description: string;
  address?: string;
  contact?: string;
  location?: string;
  bank_details?: string;
  logo: string;
  other_details?: string;
  link_name: string;
  public: boolean;
  active: boolean;
  whitelabelled: boolean;
  logo_url: string;
  country_id: number;
  country: string;
  total_classes: number;
}

export interface ClassBundlePriceListItem {
  currency: string;
  year: string;
  month: string;
  years: string;
}

export interface ClassBundleClassItem {
  class_points: number;
  id: number;
  content_provider_id: number;
  name: string;
  link_name: string;
  description: string;
  goals: string[];
  skills: string[];
  requirements: string[] | null;
  runtime: number;
  subscription_amount_year: number;
  subscription_amount_years: number;
  subscription_amount_month: number;
  subscription_currency: string | null;
  free_period: number;
  public: boolean;
  active: boolean;
  logo_url: string;
  language: string;
  rating: number;
  has_paid: boolean;
  has_expired: boolean;
  start_date: string;
  end_date: string;
  is_subscribed: boolean;
  payment_url: string;
  price_list: ClassBundlePriceListItem[];
}

export interface ClassBundleResponse {
  id: string;
  name: string;
  description: string;
  subscription_amount_year: number;
  subscription_amount_years: number;
  subscription_amount_month: number;
  subscription_currency: string;
  classes: ClassBundleClassItem[];
  is_active: boolean;
  updated_at: string;
}

export function isBundleResponse(
  data: ClassResponse | ClassBundleResponse,
): data is ClassBundleResponse {
  return "classes" in data && Array.isArray(data.classes);
}

export interface Point {
  content_id: number;
  name: string;
  description: string;
  where: string;
  marks: number;
}

export interface PointResponse {
  points: Array<Point>;
  point_class: ClassResponse;
  content_provider: ContentProviderResponse;
}

export interface UserPointsResponse extends DateTimeResponse {
  id: number;
  user_id: number;
  class_id: number;
  content_id: number;
  where: string;
  marks: number;
}
export interface PointClassResponse extends ClassResponse {
  class_points: number;
}

export interface PointClassDetailsResponse extends CourseResponse {
  content_points: number;
  class_id: number;
  id: number;
  points: Array<Point>;
}

export interface PointTestResponse extends DateTimeResponse {
  id: number;
  user_id: number;
  class_id: number;
  content_id: number;
  where: string;
  marks: number;
  point_class: ClassResponse;
  content: CourseResponse;
}

export interface ResourceResponse extends DateTimeResponse {
  id: number;
  content_id: number;
  description: string;
  name: string;
  type: ResourceTypes;
  url: string;
  duration: 0 | number;
  durationCountDown: 0 | number;
  currentTime: 0 | number;
}

export interface TestResponse extends DateTimeResponse {
  id: number;
  instructions: string;
  description: string;
  name: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  duration: number;
  question_limit: number;
  pass_mark: number;
  active: boolean;
  multiple_times: boolean;
  multiple_occurrence: boolean;
  occurrence_time: number;
  user_results: Array<ResultResponse>;
  test_url: string;
  is_test_available: boolean;
  test_class: ClassResponse;
  test_classes: Array<ClassResponse>;
}

export interface TestResultResponse extends DateTimeResponse {
  id: number;
  marks: number;
  no_of_questions: number;
  test_id: number;
  time_taken: number;
  user_id: number;
}

export interface ContentResponse extends DateTimeResponse {
  id: number;
  class_id: number;
  order: number;
  has_practice: boolean;
  has_read: boolean;
  has_test: boolean;
  has_content: boolean;
  practice_url: string;
  read_url: string;
  topic?: ContentResponse;
  topics: Array<ContentResponse>;
  content_class: ClassResponse;
  content_resources: Array<ResourceResponse>;
  user_points: Array<PointResponse | UserPointsResponse>;
  tests: Array<TestResponse>;
  text_content: string;
  description: string;
  title: string;
  published: boolean;
  total_topics: number;
  total_audios: number;
  total_videos: number;
  content_provider_id: number;
}

export interface ResultResponse extends DateTimeResponse {
  id: number;
  test_id: number;
  user_id: number;
  time_taken: number;
  marks: number;
  no_of_questions: number;
}

export interface AnnouncementResponse extends DateTimeResponse {
  id: number;
  title: string;
  details: string;
  date: string;
  resource_url?: string;
  resource_type?: string;
}

export interface EventResponse extends DateTimeResponse {
  id: number;
  title: string;
  details: string;
  date: string;
  time: string;
  venue?: string;
  resource_url?: string;
  resource_type?: string;
}

export interface SearchResponse {
  classes: Array<ClassResponse>;
  classes_count: number;
  content_providers: Array<ContentProviderResponse>;
  content_provider_count: number;
  contents: Array<ContentResponse>;
  contents_count: number;
  error: boolean;
  message: string;
  search_text: string;
  tests: Array<TestResponse>;
  tests_count: number;
  [key: string]: any;
}

export interface ClassContentDiscussionResponse extends DateTimeResponse {
  id: number;
  content_id: number;
  post: string;
  published: boolean;
  reported: boolean;
  total_comments: number;
  user: UserResponse;
  resources: ResourceResponse[];
}

export interface DiscussionCommentResponse extends ClassContentDiscussionResponse {
  comment: string;
  correct_answer: boolean;
}

export interface CertificateModelResponse extends DateTimeResponse {
  id: string;
  certificate_number: string;
  certificate: CertificateResponse;
  view_url: string;
  pdf_url: string;
  image_url: string;
}
export interface CertificateResponse extends DateTimeResponse {
  id: string;
  name: string;
  title: string;
  description: string;
  introduction: string;
  for: string;
  for_id: string;
  logo_url: string | null;
  background_url: string | null;
  signatures: string | null;
}
